import React from "react";
import { useShallow } from "zustand/react/shallow";
import { useViewerActions, useViewerStore } from "../../../stores/viewerStore";
import { useWindowEventState } from "../../../hooks/useWindowEventState";
import {
  BODY_VISIBILITY_UI_EVENT,
  getBodyVisibilityUiState,
  setBodyVisibilityUiState,
} from "../../../selected-body-visibility";
import {
  JOINT_VISIBILITY_UI_EVENT,
  getJointVisibilityUiState,
  setJointVisibilityUiState,
} from "../../../selected-joint-visibility";
import {
  SITE_VISIBILITY_UI_EVENT,
  getSiteVisibilityUiState,
  setSiteVisibilityUiState,
} from "../../../selected-site-visibility";
import {
  CONSTRAINT_VISIBILITY_UI_EVENT,
  getConstraintVisibilityUiState,
  setConstraintVisibilityUiState,
} from "../../../selected-constraint-visibility/visibilityEvents";
import {
  SCENE_INVENTORY_LABEL_VISIBILITY_EVENT,
  getSceneInventoryLabelVisibilityState,
  setSceneInventoryLabelVisibilityState,
} from "../../../scene-inventory-labels";
import {
  VIEWPORT_BODY_VISIBILITY_EVENT,
  VIEWPORT_CAMERA_VISIBILITY_EVENT,
  dispatchViewportCameraVisibility,
} from "./sceneInventoryViewportEvents";

export function useSceneInventoryVisibility() {
  const [bodyVisibilityUi, setBodyVisibilityUi] = useWindowEventState(
    BODY_VISIBILITY_UI_EVENT,
    getBodyVisibilityUiState
  );
  const [jointVisibilityUi, setJointVisibilityUi] = useWindowEventState(
    JOINT_VISIBILITY_UI_EVENT,
    getJointVisibilityUiState
  );
  const [siteVisibilityUi, setSiteVisibilityUi] = useWindowEventState(
    SITE_VISIBILITY_UI_EVENT,
    getSiteVisibilityUiState
  );
  const [constraintVisibilityUi, setConstraintVisibilityUi] = useWindowEventState(
    CONSTRAINT_VISIBILITY_UI_EVENT,
    getConstraintVisibilityUiState
  );
  const [inventoryLabelVisibility, setInventoryLabelVisibility] = useWindowEventState(
    SCENE_INVENTORY_LABEL_VISIBILITY_EVENT,
    getSceneInventoryLabelVisibilityState
  );
  const [showViewportBodies, setShowViewportBodies] = React.useState(true);
  const [showCameras, setShowCameras] = React.useState(false);
  const {
    showCollision,
    showSites,
    showVisual,
    showJoints,
    showInertials,
    showConstraintMarkers,
    showActuators,
    bodyVisibility,
    jointVizVisibility,
    meshVisibility,
    collisionGeomVisibility,
    siteVisibility,
    inertialVisibility,
    equalityMarkerVisibility,
    tendonMarkerVisibility,
    cameraMarkerVisibility,
  } = useViewerStore(
    useShallow((s) => ({
      showCollision: s.showCollision,
      showSites: s.showSites,
      showVisual: s.showVisual,
      showJoints: s.showJoints,
      showInertials: s.showInertials,
      showConstraintMarkers: s.showConstraintMarkers,
      showActuators: s.showActuators,
      bodyVisibility: s.bodyVisibility,
      jointVizVisibility: s.jointVizVisibility,
      meshVisibility: s.meshVisibility,
      collisionGeomVisibility: s.collisionGeomVisibility,
      siteVisibility: s.siteVisibility,
      inertialVisibility: s.inertialVisibility,
      equalityMarkerVisibility: s.equalityMarkerVisibility,
      tendonMarkerVisibility: s.tendonMarkerVisibility,
      cameraMarkerVisibility: s.cameraMarkerVisibility,
    }))
  );
  const viewerActions = useViewerActions();
  const constraintLayerVisible =
    showConstraintMarkers && constraintVisibilityUi.constraintsVisible !== false;
  const tendonLayerVisible =
    showConstraintMarkers && constraintVisibilityUi.tendonsVisible !== false;

  const setConstraintLayerVisible = React.useCallback(
    (visible) => {
      setConstraintVisibilityUi(
        setConstraintVisibilityUiState({
          constraintsVisible: !!visible,
          ...(!showConstraintMarkers && visible ? { tendonsVisible: false } : {}),
        })
      );
      if (visible && !showConstraintMarkers) {
        viewerActions.toggleShowConstraintMarkers();
      }
      if (!visible && !getConstraintVisibilityUiState().tendonsVisible && showConstraintMarkers) {
        viewerActions.toggleShowConstraintMarkers();
      }
    },
    [setConstraintVisibilityUi, showConstraintMarkers, viewerActions]
  );

  const setTendonLayerVisible = React.useCallback(
    (visible) => {
      setConstraintVisibilityUi(
        setConstraintVisibilityUiState({
          tendonsVisible: !!visible,
          ...(!showConstraintMarkers && visible ? { constraintsVisible: false } : {}),
        })
      );
      if (visible && !showConstraintMarkers) {
        viewerActions.toggleShowConstraintMarkers();
      }
      if (
        !visible &&
        !getConstraintVisibilityUiState().constraintsVisible &&
        showConstraintMarkers
      ) {
        viewerActions.toggleShowConstraintMarkers();
      }
    },
    [setConstraintVisibilityUi, showConstraintMarkers, viewerActions]
  );

  React.useEffect(() => {
    const handler = (event) => {
      setShowViewportBodies((event?.detail?.visible ?? true) === true);
    };
    window.addEventListener(VIEWPORT_BODY_VISIBILITY_EVENT, handler);
    return () => window.removeEventListener(VIEWPORT_BODY_VISIBILITY_EVENT, handler);
  }, []);

  React.useEffect(() => {
    const handler = (event) => {
      setShowCameras((event?.detail?.visible ?? false) === true);
    };
    window.addEventListener(VIEWPORT_CAMERA_VISIBILITY_EVENT, handler);
    return () => window.removeEventListener(VIEWPORT_CAMERA_VISIBILITY_EVENT, handler);
  }, []);

  const getTypeVisible = React.useCallback(
    (rowKey) => {
      switch (rowKey) {
        case "object":
        case "body":
          return showViewportBodies;
        case "joint":
          return showJoints;
        case "actuator":
          return showActuators;
        case "mesh":
        case "visual":
          return showVisual;
        case "collision":
          return showCollision;
        case "site":
          return showSites;
        case "inertial":
          return showInertials;
        case "constraint":
          return constraintLayerVisible;
        case "tendon":
          return tendonLayerVisible;
        case "camera":
          return showCameras;
        default:
          return null;
      }
    },
    [
      showActuators,
      showCameras,
      showCollision,
      constraintLayerVisible,
      showInertials,
      showJoints,
      showSites,
      tendonLayerVisible,
      showViewportBodies,
      showVisual,
    ]
  );

  const toggleTypeVisibility = React.useCallback(
    (rowKey) => {
      switch (rowKey) {
        case "object":
        case "body": {
          const visible = !showViewportBodies;
          setShowViewportBodies(visible);
          window.dispatchEvent(
            new CustomEvent(VIEWPORT_BODY_VISIBILITY_EVENT, { detail: { visible } })
          );
          break;
        }
        case "joint":
          viewerActions.toggleShowJoints();
          break;
        case "actuator":
          viewerActions.toggleShowActuators();
          break;
        case "mesh":
        case "visual":
          viewerActions.toggleShowVisual();
          break;
        case "collision":
          viewerActions.toggleShowCollision();
          break;
        case "site":
          viewerActions.toggleShowSites();
          break;
        case "inertial":
          viewerActions.toggleShowInertials();
          break;
        case "constraint":
          setConstraintLayerVisible(!constraintLayerVisible);
          break;
        case "tendon":
          setTendonLayerVisible(!tendonLayerVisible);
          break;
        case "camera": {
          const visible = !showCameras;
          setShowCameras(visible);
          dispatchViewportCameraVisibility(visible);
          break;
        }
      }
    },
    [
      constraintLayerVisible,
      setConstraintLayerVisible,
      setTendonLayerVisible,
      showCameras,
      showViewportBodies,
      tendonLayerVisible,
      viewerActions,
    ]
  );

  const getItemHidden = React.useCallback(
    (rowKey, targetId) => {
      switch (rowKey) {
        case "object":
        case "body":
          return bodyVisibility[targetId] === false;
        case "joint":
          return jointVizVisibility[targetId] === false;
        case "mesh":
          return meshVisibility[targetId] === false;
        case "visual":
        case "collision":
          return collisionGeomVisibility[targetId] === false;
        case "site":
          return siteVisibility[targetId] === false;
        case "inertial":
          return inertialVisibility[targetId] === false;
        case "constraint":
          return equalityMarkerVisibility[targetId] === false;
        case "tendon":
          return tendonMarkerVisibility[targetId] === false;
        case "camera":
          return cameraMarkerVisibility[targetId] === false;
        default:
          return false;
      }
    },
    [
      bodyVisibility,
      jointVizVisibility,
      meshVisibility,
      collisionGeomVisibility,
      siteVisibility,
      inertialVisibility,
      equalityMarkerVisibility,
      tendonMarkerVisibility,
      cameraMarkerVisibility,
    ]
  );

  const toggleItemVisibility = React.useCallback(
    (rowKey, targetId, isHidden) => {
      // Viewer stores use visible=true for shown; `isHidden` is whether the row is hidden now.
      const visible = isHidden;
      switch (rowKey) {
        case "object":
        case "body":
          viewerActions.setBodyVisibility(targetId, visible);
          break;
        case "joint":
          viewerActions.setJointVizVisible(targetId, visible);
          break;
        case "mesh":
          viewerActions.setMeshVisibility(targetId, visible);
          break;
        case "visual":
        case "collision":
          viewerActions.setCollisionGeomVisibility(targetId, visible);
          break;
        case "site":
          viewerActions.setSiteVisibility(targetId, visible);
          break;
        case "inertial":
          viewerActions.setInertialVisibility(targetId, visible);
          break;
        case "constraint":
          viewerActions.setEqualityMarkerVisibility(targetId, visible);
          break;
        case "tendon":
          viewerActions.setTendonMarkerVisibility(targetId, visible);
          break;
        case "camera":
          viewerActions.setCameraMarkerVisibility(targetId, visible);
          if (visible) dispatchViewportCameraVisibility(true);
          break;
      }
    },
    [viewerActions]
  );

  const toggleTypeLabels = React.useCallback(
    (rowKey) => {
      if (rowKey === "object") {
        setBodyVisibilityUi(
          setBodyVisibilityUiState({ objectLabels: !getBodyVisibilityUiState().objectLabels })
        );
        return;
      }
      if (rowKey === "body") {
        setBodyVisibilityUi(
          setBodyVisibilityUiState({ labels: !getBodyVisibilityUiState().labels })
        );
        return;
      }
      if (rowKey === "joint") {
        setJointVisibilityUi(
          setJointVisibilityUiState({ labelsOff: !getJointVisibilityUiState().labelsOff })
        );
        return;
      }
      if (rowKey === "site") {
        setSiteVisibilityUi(
          setSiteVisibilityUiState({ labelsOff: !getSiteVisibilityUiState().labelsOff })
        );
        return;
      }
      if (rowKey === "constraint") {
        setConstraintVisibilityUi(
          setConstraintVisibilityUiState({
            labelsOff: !getConstraintVisibilityUiState().labelsOff,
          })
        );
        return;
      }
      if (rowKey === "tendon") {
        setConstraintVisibilityUi(
          setConstraintVisibilityUiState({
            tendonLabelsOff: !getConstraintVisibilityUiState().tendonLabelsOff,
          })
        );
        return;
      }
      if (
        rowKey === "actuator" ||
        rowKey === "mesh" ||
        rowKey === "visual" ||
        rowKey === "collision" ||
        rowKey === "inertial" ||
        rowKey === "camera"
      ) {
        const current = getSceneInventoryLabelVisibilityState();
        setInventoryLabelVisibility(
          setSceneInventoryLabelVisibilityState({ [rowKey]: !current[rowKey] })
        );
      }
    },
    [
      setBodyVisibilityUi,
      setConstraintVisibilityUi,
      setInventoryLabelVisibility,
      setJointVisibilityUi,
      setSiteVisibilityUi,
    ]
  );

  const getLabelToggleActive = React.useCallback(
    (rowKey) => {
      if (rowKey === "object") return bodyVisibilityUi.objectLabels;
      if (rowKey === "body") return bodyVisibilityUi.labels;
      if (rowKey === "joint") return !jointVisibilityUi.labelsOff;
      if (rowKey === "site") return !siteVisibilityUi.labelsOff;
      if (rowKey === "constraint") return !constraintVisibilityUi.labelsOff;
      if (rowKey === "tendon") return !constraintVisibilityUi.tendonLabelsOff;
      if (
        rowKey === "actuator" ||
        rowKey === "mesh" ||
        rowKey === "visual" ||
        rowKey === "collision" ||
        rowKey === "inertial" ||
        rowKey === "camera"
      ) {
        return !!inventoryLabelVisibility[rowKey];
      }
      return null;
    },
    [
      bodyVisibilityUi.labels,
      bodyVisibilityUi.objectLabels,
      constraintVisibilityUi.labelsOff,
      constraintVisibilityUi.tendonLabelsOff,
      inventoryLabelVisibility,
      jointVisibilityUi.labelsOff,
      siteVisibilityUi.labelsOff,
    ]
  );

  return {
    getItemHidden,
    getLabelToggleActive,
    getTypeVisible,
    toggleItemVisibility,
    toggleTypeLabels,
    toggleTypeVisibility,
  };
}
